import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as api from '../utils/api';
import VersionManager from './VersionManager';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  width: 90vw;
  height: 90vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
`;

const Header = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.$darkMode ? '#161b22' : '#f6f8fa'};
`;

const Title = styled.h2`
  margin: 0;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 24px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:before {
    content: '📚';
    font-size: 28px;
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.$darkMode ? '#8b949e' : '#57606a'};
`;

const Toolbar = styled.div`
  padding: 20px 32px;
  border-bottom: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  background: ${props => props.$darkMode ? '#161b22' : '#f6f8fa'};
`;

const Btn = styled.button`
  padding: 10px 18px;
  border: 2px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 8px;
  background: ${props => props.$darkMode ? '#21262d' : '#fff'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$darkMode ? '#30363d' : '#f6f8fa'};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const Stats = styled.div`
  margin-left: auto;
  color: ${props => props.$darkMode ? '#8b949e' : '#57606a'};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  border-radius: 8px;
  border: 2px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
`;

const Search = styled.input`
  flex: 1;
  min-width: 250px;
  padding: 10px 16px 10px 40px;
  border: 2px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 8px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="%238b949e" viewBox="0 0 16 16"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/></svg>');
  background-repeat: no-repeat;
  background-position: 12px center;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
  }
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 2px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 8px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
  transform: ${props => props.$flipped ? 'rotateY(180deg)' : 'rotateY(0)'};
  min-height: 280px;
  position: relative;
  box-shadow: ${props => props.$darkMode ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.08)'};
  z-index: ${props => props.$flipped ? '10' : '1'};
  
  &:hover {
    border-color: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
    box-shadow: ${props => props.$darkMode ? '0 12px 32px rgba(88,166,255,0.2)' : '0 12px 32px rgba(9,105,218,0.15)'};
    transform: ${props => props.$flipped ? 'rotateY(180deg) translateY(-8px)' : 'translateY(-8px)'};
    z-index: ${props => props.$flipped ? '10' : '5'};
  }
`;

const CardFront = styled.div`
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 28px;
  display: flex;
  flex-direction: column;
  background: ${props => props.$darkMode ? 'linear-gradient(135deg, #161b22 0%, #1c2128 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f6f8fa 100%)'};
  border-radius: 16px;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0969da, #58a6ff, #bc8cff);
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  ${Card}:hover &:before {
    opacity: 1;
  }
`;

const CardBack = styled.div`
  backface-visibility: hidden;
  transform: rotateY(180deg);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${props => props.$darkMode ? 'linear-gradient(135deg, #1c2128 0%, #161b22 100%)' : 'linear-gradient(135deg, #f6f8fa 0%, #ffffff 100%)'};
  border-radius: 16px;
  overflow-y: auto;
  max-height: 100%;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px 0;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
`;

const CardDesc = styled.p`
  margin: 0;
  color: ${props => props.$darkMode ? '#8b949e' : '#57606a'};
  font-size: 14px;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
`;

const Tag = styled.span`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$darkMode ? 'rgba(88,166,255,0.1)' : 'rgba(9,105,218,0.08)'};
  color: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
  border: 1px solid ${props => props.$darkMode ? 'rgba(88,166,255,0.2)' : 'rgba(9,105,218,0.15)'};
`;

const ActionBtn = styled.button`
  padding: 14px 18px;
  border: none;
  border-radius: 10px;
  background: ${props => props.$primary ? 'linear-gradient(135deg, #0969da 0%, #0860ca 100%)' : (props.$darkMode ? '#21262d' : '#f6f8fa')};
  color: ${props => props.$primary ? '#fff' : (props.$darkMode ? '#e6edf3' : '#24292f')};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s;
  text-align: left;
  box-shadow: ${props => props.$primary ? '0 2px 8px rgba(9,105,218,0.3)' : 'none'};
  
  &:hover {
    background: ${props => props.$primary ? 'linear-gradient(135deg, #0860ca 0%, #0757b8 100%)' : (props.$darkMode ? '#30363d' : '#eaeef2')};
    transform: translateX(4px);
    box-shadow: ${props => props.$primary ? '0 4px 12px rgba(9,105,218,0.4)' : '0 2px 8px rgba(0,0,0,0.1)'};
  }
`;

function SpecsLibrary({ darkMode, onClose, onLoad }) {
  const [specs, setSpecs] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [flipped, setFlipped] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [showVersions, setShowVersions] = useState(null);
  const [versions, setVersions] = useState({});
  const [selectedVersions, setSelectedVersions] = useState({});
  const isGuest = localStorage.getItem('auth-token') === 'guest-token';

  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token && token !== 'guest-token') {
      api.getSpecs().then(data => {
        setSpecs(data);
        data.forEach(spec => loadVersions(spec.id));
      }).catch(() => {});
    } else {
      const localSpecs = JSON.parse(localStorage.getItem('local-specs') || '[]');
      setSpecs(localSpecs);
    }
  }, []);

  const loadVersions = async (specId) => {
    try {
      const data = await api.getSpecVersions(specId);
      setVersions(prev => ({ ...prev, [specId]: data }));
      if (data.length > 0 && !selectedVersions[specId]) {
        const sortedVersions = [...data].sort((a, b) => {
          const versionA = a.version.split('.').map(Number);
          const versionB = b.version.split('.').map(Number);
          for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const diff = (versionB[i] || 0) - (versionA[i] || 0);
            if (diff !== 0) return diff;
          }
          return 0;
        });
        setSelectedVersions(prev => ({ ...prev, [specId]: sortedVersions[0].id }));
      }
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const categories = ['all', ...new Set(specs.map(s => s.category).filter(Boolean))];

  const filtered = specs
    .filter(s => {
      const matchSearch = !search || s.title.toLowerCase().includes(search.toLowerCase()) || s.description?.toLowerCase().includes(search.toLowerCase());
      const matchCategory = category === 'all' || s.category === category;
      return matchSearch && matchCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.savedAt) - new Date(a.savedAt);
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'endpoints') return (b.endpoints || 0) - (a.endpoints || 0);
      if (sortBy === 'live') {
        const aHasLive = versions[a.id]?.some(v => v.is_live) ? 1 : 0;
        const bHasLive = versions[b.id]?.some(v => v.is_live) ? 1 : 0;
        return bHasLive - aHasLive;
      }
      return 0;
    });

  const handleView = (spec, viewer) => {
    setFlipped(null);
    onLoad(spec, viewer);
  };

  const handleEdit = (spec) => {
    setFlipped(null);
    onLoad(spec, 'edit');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this spec?')) return;
    await api.deleteSpec(id);
    const updated = specs.filter(s => s.id !== id);
    setSpecs(updated);
    const token = localStorage.getItem('auth-token');
    if (!token || token === 'guest-token') {
      localStorage.setItem('local-specs', JSON.stringify(updated));
    }
    setFlipped(null);
  };

  const handleDuplicate = async (spec) => {
    const duplicate = {
      id: Date.now().toString(),
      title: spec.title + ' (Copy)',
      description: spec.description,
      category: spec.category,
      spec: spec.spec,
      endpoints: spec.endpoints,
      savedAt: new Date().toISOString()
    };
    const saved = await api.createSpec(duplicate);
    const updated = [saved, ...specs];
    setSpecs(updated);
    const token = localStorage.getItem('auth-token');
    if (!token || token === 'guest-token') {
      localStorage.setItem('local-specs', JSON.stringify(updated));
    }
    setFlipped(null);
  };

  const handleExport = (spec) => {
    const blob = new Blob([spec.spec], { type: 'application/x-yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = spec.title.replace(/[^a-z0-9]/gi, '-').toLowerCase() + '.yaml';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    const data = JSON.stringify(specs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'specs-library.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportLibrary = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        const token = localStorage.getItem('auth-token');
        if (token && token !== 'guest-token') {
          for (const spec of imported) {
            await api.createSpec(spec);
          }
          const allSpecs = await api.getSpecs();
          setSpecs(allSpecs);
        } else {
          const updated = [...imported, ...specs];
          setSpecs(updated);
          localStorage.setItem('local-specs', JSON.stringify(updated));
        }
        alert('Library imported!');
      } catch (err) {
        alert('Error importing: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  return (
    <>
      {showVersions && (
        <VersionManager 
          specId={showVersions} 
          onClose={() => setShowVersions(null)}
          onSelectVersion={(version) => {
            const spec = specs.find(s => s.id === showVersions);
            handleView({...spec, spec: version.content}, 'custom');
            setShowVersions(null);
          }}
        />
      )}
      <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
        <Modal $darkMode={darkMode}>
        <Header $darkMode={darkMode}>
          <Title $darkMode={darkMode}>Specs Library</Title>
          <CloseBtn $darkMode={darkMode} onClick={onClose}>&times;</CloseBtn>
        </Header>
        
        <Toolbar $darkMode={darkMode}>
          <Search 
            $darkMode={darkMode}
            placeholder="Search specs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select $darkMode={darkMode} value={category} onChange={e => setCategory(e.target.value)}>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
            ))}
          </Select>
          <Select $darkMode={darkMode} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="date">Sort: Date</option>
            <option value="title">Sort: Title</option>
            <option value="endpoints">Sort: Endpoints</option>
            <option value="live">Sort: Live Status</option>
          </Select>
          <Btn $darkMode={darkMode} onClick={handleExportAll}>📤 Export All</Btn>
          <Btn $darkMode={darkMode} onClick={() => document.getElementById('import-lib').click()}>📥 Import</Btn>
          <input id="import-lib" type="file" accept=".json" style={{display: 'none'}} onChange={handleImportLibrary} />
          <Stats $darkMode={darkMode}>{filtered.length} of {specs.length} specs</Stats>
        </Toolbar>

        <Content>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: darkMode ? '#8b949e' : '#57606a' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{specs.length === 0 ? '📚' : '🔍'}</div>
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                {specs.length === 0 ? 'No saved specs yet' : 'No specs found'}
              </div>
              <div style={{ fontSize: '14px' }}>
                {specs.length === 0 ? 'Save your first spec to get started!' : 'Try adjusting your search or filters.'}
              </div>
            </div>
          ) : (
            <Grid>
              {filtered.map(spec => (
                <Card 
                  key={spec.id} 
                  $darkMode={darkMode}
                  $flipped={flipped === spec.id}
                  onClick={() => setFlipped(flipped === spec.id ? null : spec.id)}
                >
                  <CardFront $darkMode={darkMode}>
                    <CardTitle $darkMode={darkMode}>{spec.title}</CardTitle>
                    <CardDesc $darkMode={darkMode}>{spec.description || 'No description'}</CardDesc>
                    <CardMeta $darkMode={darkMode}>
                      {spec.category && <Tag $darkMode={darkMode}>{spec.category}</Tag>}
                      <Tag $darkMode={darkMode}>{spec.endpoints || 0} endpoints</Tag>
                      {!isGuest && versions[spec.id] && (() => {
                        const hasLive = versions[spec.id].some(v => v.is_live);
                        const hasLatestLive = versions[spec.id].some(v => v.is_latest_live);
                        const hasPublic = versions[spec.id].some(v => v.is_public);
                        return (
                          <>
                            {hasLatestLive && <Tag $darkMode={darkMode} style={{background: 'rgba(76,175,80,0.1)', color: '#4caf50', borderColor: 'rgba(76,175,80,0.2)'}}>Latest Live</Tag>}
                            {hasLive && !hasLatestLive && <Tag $darkMode={darkMode} style={{background: 'rgba(33,150,243,0.1)', color: '#2196f3', borderColor: 'rgba(33,150,243,0.2)'}}>Live</Tag>}
                            {!hasLive && <Tag $darkMode={darkMode} style={{background: 'rgba(158,158,158,0.1)', color: '#9e9e9e', borderColor: 'rgba(158,158,158,0.2)'}}>Draft</Tag>}
                            {hasPublic && <Tag $darkMode={darkMode} style={{background: 'rgba(255,152,0,0.1)', color: '#ff9800', borderColor: 'rgba(255,152,0,0.2)'}}>🌐 Public</Tag>}
                          </>
                        );
                      })()}
                      <Tag $darkMode={darkMode}>{new Date(spec.savedAt).toLocaleDateString()}</Tag>
                    </CardMeta>
                  </CardFront>
                  
                  <CardBack $darkMode={darkMode} onClick={() => setFlipped(null)}>
                    <CardTitle $darkMode={darkMode} style={{fontSize: '18px', marginBottom: '8px'}}>Choose Action</CardTitle>
                    {!isGuest && versions[spec.id] && versions[spec.id].length > 0 && (() => {
                      const sortedVersions = [...versions[spec.id]].sort((a, b) => {
                        const versionA = a.version.split('.').map(Number);
                        const versionB = b.version.split('.').map(Number);
                        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
                          const diff = (versionB[i] || 0) - (versionA[i] || 0);
                          if (diff !== 0) return diff;
                        }
                        return 0;
                      });
                      const latestVersion = sortedVersions[0];
                      const currentVersion = selectedVersions[spec.id] || latestVersion.id;
                      return (
                        <div style={{marginBottom: '12px'}} onClick={e => e.stopPropagation()}>
                          <Select $darkMode={darkMode} style={{width: '100%', fontSize: '12px'}} 
                            value={currentVersion}
                            onChange={(e) => {
                              const versionId = parseInt(e.target.value);
                              setSelectedVersions(prev => ({ ...prev, [spec.id]: versionId }));
                            }}>
                            {sortedVersions.map(v => (
                              <option key={v.id} value={v.id}>
                                v{v.version} {v.is_latest_live ? '(Latest Live)' : v.is_live ? '(Live)' : '(Draft)'} {v.is_public ? '🌐' : ''}
                              </option>
                            ))}
                          </Select>
                        </div>
                      );
                    })()}
                    {!isGuest && <ActionBtn $darkMode={darkMode} onClick={(e) => { e.stopPropagation(); setShowVersions(spec.id); }}>📋 Manage Versions ({spec.version_count || 0})</ActionBtn>}
                    <ActionBtn $darkMode={darkMode} $primary onClick={(e) => {
                      e.stopPropagation();
                      const versionId = selectedVersions[spec.id];
                      if (versionId && versions[spec.id]) {
                        const version = versions[spec.id].find(v => v.id === versionId);
                        if (version) {
                          setFlipped(null);
                          onLoad({...spec, spec: version.content, spec_name: spec.spec_name || spec.title}, 'edit', version);
                          return;
                        }
                      }
                      handleEdit(spec);
                    }}>✏️ Edit</ActionBtn>
                    <ActionBtn $darkMode={darkMode} onClick={(e) => {
                      e.stopPropagation();
                      const versionId = selectedVersions[spec.id];
                      if (versionId && versions[spec.id]) {
                        const version = versions[spec.id].find(v => v.id === versionId);
                        if (version) return handleView({...spec, spec: version.content}, 'custom');
                      }
                      handleView(spec, 'custom');
                    }} style={{display: 'flex', alignItems: 'center'}}><img src={require('../Dlogomin.png')} alt="" style={{height: '16px', marginRight: '6px'}} /> Deviant Viewer</ActionBtn>
                    <ActionBtn $darkMode={darkMode} onClick={(e) => {
                      e.stopPropagation();
                      const versionId = selectedVersions[spec.id];
                      if (versionId && versions[spec.id]) {
                        const version = versions[spec.id].find(v => v.id === versionId);
                        if (version) return handleView({...spec, spec: version.content}, 'swagger');
                      }
                      handleView(spec, 'swagger');
                    }}>🔍 Swagger UI</ActionBtn>
                    <ActionBtn $darkMode={darkMode} onClick={(e) => {
                      e.stopPropagation();
                      const versionId = selectedVersions[spec.id];
                      if (versionId && versions[spec.id]) {
                        const version = versions[spec.id].find(v => v.id === versionId);
                        if (version) return handleView({...spec, spec: version.content}, 'redoc');
                      }
                      handleView(spec, 'redoc');
                    }}>📖 Redoc</ActionBtn>
                    <ActionBtn $darkMode={darkMode} onClick={(e) => {
                      e.stopPropagation();
                      const versionId = selectedVersions[spec.id];
                      if (versionId && versions[spec.id]) {
                        const version = versions[spec.id].find(v => v.id === versionId);
                        if (version) {
                          const blob = new Blob([version.content], { type: 'application/x-yaml' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${spec.title}-v${version.version}.yaml`;
                          a.click();
                          URL.revokeObjectURL(url);
                          return;
                        }
                      }
                      handleExport(spec);
                    }}>💾 Export YAML</ActionBtn>
                    <ActionBtn $darkMode={darkMode} onClick={(e) => { e.stopPropagation(); handleDuplicate(spec); }}>📋 Duplicate</ActionBtn>
                    <ActionBtn $darkMode={darkMode} style={{ marginTop: 'auto', color: '#cf222e' }} onClick={(e) => { e.stopPropagation(); handleDelete(spec.id); }}>🗑️ Delete</ActionBtn>
                  </CardBack>
                </Card>
              ))}
            </Grid>
          )}
        </Content>
      </Modal>
    </Overlay>
    </>
  );
}

export default SpecsLibrary;
